import { action, computed, makeObservable, observable } from 'mobx'
import {
  CheckTaxRefundDeliveryMethod,
  DirectDepositTaxRefundDeliveryMethod,
  FutureTaxCreditTaxRefundDeliveryMethod,
  TaxRefundMethodVersion,
} from '../TaxRefundMethodVersion'
import {
  DeliveryMethodInput,
  bankAccountTypeFromString,
} from '../taxRefundMethodGrpcDtoMapper'
import CheckDeliveryMethodEditorViewModel from './CheckDeliveryMethodEditorViewModel'
import DirectDepositDeliveryMethodEditorViewModel from './DirectDepositDeliveryMethodEditorViewModel'
import FutureTaxCreditDeliveryMethodEditorViewModel from './FutureTaxCreditDeliveryMethodEditorViewModel'

export type DeliveryMethodKind = 'directDeposit' | 'check' | 'futureTaxCredit'

export type RefundKind = 'personal' | 'business'

export default class TaxRefundMethodEditorViewModel {
  readonly refundType: RefundKind

  readonly directDepositVm = new DirectDepositDeliveryMethodEditorViewModel()

  readonly checkVm = new CheckDeliveryMethodEditorViewModel()

  readonly futureTaxCreditVm =
    new FutureTaxCreditDeliveryMethodEditorViewModel()

  @observable selectedMethod: DeliveryMethodKind | null = null

  @observable selectionError: string | null = null

  constructor(refundType: RefundKind) {
    this.refundType = refundType
    makeObservable(this)
  }

  @computed
  get availableMethods(): DeliveryMethodKind[] {
    return this.refundType === 'business'
      ? ['directDeposit', 'check', 'futureTaxCredit']
      : ['directDeposit', 'check']
  }

  @action.bound
  selectMethod(method: DeliveryMethodKind) {
    this.selectedMethod = method
    this.selectionError = null
  }

  @action.bound
  validate() {
    if (!this.selectedMethod) {
      this.selectionError = 'Please choose a refund method'
      return false
    }
    this.selectionError = null
    switch (this.selectedMethod) {
      case 'directDeposit':
        return this.directDepositVm.validate()
      case 'check':
        return this.checkVm.validate()
      case 'futureTaxCredit':
        return this.futureTaxCreditVm.validate()
    }
  }

  @action.bound
  reset(profile?: TaxRefundMethodVersion | null) {
    const deliveryMethod = profile?.deliveryMethod

    if (deliveryMethod instanceof DirectDepositTaxRefundDeliveryMethod) {
      this.selectedMethod = 'directDeposit'
      const bankAccount = deliveryMethod.bankAccount
      this.directDepositVm.reset({
        bankAccount: bankAccount
          ? {
              accountType: bankAccountTypeFromString(bankAccount.accountType),
              accountNumber: bankAccount.accountNumber,
              routingNumber: bankAccount.routingNumber,
              bankName: bankAccount.bankName ?? '',
            }
          : undefined,
      })
      this.checkVm.reset(undefined)
      this.futureTaxCreditVm.reset()
    } else if (deliveryMethod instanceof CheckTaxRefundDeliveryMethod) {
      this.selectedMethod = 'check'
      const mailingAddress = deliveryMethod.mailingAddress
      this.checkVm.reset({
        mailingAddress: mailingAddress
          ? {
              addressLine1: mailingAddress.addressLine1,
              addressLine2: mailingAddress.addressLine2,
              city: mailingAddress.city,
              region: mailingAddress.region,
              postalCode: mailingAddress.postalCode,
              country: mailingAddress.country,
            }
          : undefined,
      })
      this.directDepositVm.reset(undefined)
      this.futureTaxCreditVm.reset()
    } else if (
      deliveryMethod instanceof FutureTaxCreditTaxRefundDeliveryMethod
    ) {
      this.selectedMethod = 'futureTaxCredit'
      this.directDepositVm.reset(undefined)
      this.checkVm.reset(undefined)
      this.futureTaxCreditVm.reset()
    } else {
      this.selectedMethod = null
      this.directDepositVm.reset(undefined)
      this.checkVm.reset(undefined)
      this.futureTaxCreditVm.reset()
    }

    this.selectionError = null
  }

  toDeliveryMethodInput(): DeliveryMethodInput {
    switch (this.selectedMethod) {
      case 'directDeposit':
        return {
          kind: 'directDeposit',
          bankAccount: this.directDepositVm.bankAccountVm.toBankAccount(),
        }
      case 'check':
        return {
          kind: 'check',
          mailingAddress: this.checkVm.useSameAddressAsContact
            ? undefined
            : this.checkVm.physicalAddressVm.toPhysicalAddress(),
        }
      case 'futureTaxCredit':
        return { kind: 'futureTaxCredit' }
      default:
        throw new Error('No delivery method selected')
    }
  }
}
